import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { Loader, EmptyState, ConfirmDialog } from '../../components/Shared';
import { useToast } from '../../context/AppContext';

// ── MESSAGES PAGE ─────────────────────────────────────────────
export function AdminMessagesPage() {
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { addToast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/contact').then(r => setMessages(r.data.data?.messages || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async id => {
    await api.put(`/contact/${id}`);
    setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: 1 } : m));
    if (selected?.id === id) setSelected(prev => ({ ...prev, is_read: 1 }));
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/contact/${confirmDelete.id}`);
      addToast('Message deleted', 'success');
      setConfirmDelete(null);
      if (selected?.id === confirmDelete.id) setSelected(null);
      load();
    } catch { addToast('Failed to delete', 'error'); }
  };

  const cardStyle = (m) => ({
    padding: '14px 18px',
    borderBottom: '1px solid var(--bg-3)',
    cursor: 'pointer',
    transition: 'background 0.15s',
    background: selected?.id === m.id ? 'var(--gold-bg)' : 'transparent',
    borderLeft: selected?.id === m.id ? '3px solid var(--gold)' : '3px solid transparent',
  });

  return (
    <div>
      <div className="mb-4">
        <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 4 }}>Messages</h2>
        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.875rem' }}>Contact form submissions from visitors</p>
      </div>

      <div className="row g-4">
        {/* List */}
        <div className="col-lg-5">
          <div className="admin-card" style={{ overflow: 'hidden', padding: 0 }}>
            {loading ? <div style={{ padding: 24 }}><Loader /></div> : messages.length === 0 ? (
              <EmptyState icon="bi-envelope" title="No messages yet" />
            ) : messages.map(m => (
              <div key={m.id} style={cardStyle(m)}
                onClick={() => { setSelected(m); if (!m.is_read) markRead(m.id); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {!m.is_read && (
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gold)', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0, marginLeft: m.is_read ? 17 : 0 }}>
                    <div style={{ fontWeight: m.is_read ? 500 : 700, fontSize: '0.875rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.subject || m.email}</div>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', flexShrink: 0 }}>
                    {new Date(m.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="col-lg-7">
          {selected ? (
            <div className="admin-card p-4">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
                <div>
                  <h5 style={{ color: 'var(--text)', margin: 0, fontFamily: 'var(--font-display)' }}>{selected.name}</h5>
                  <div style={{ color: 'var(--gold-dark)', fontSize: '0.875rem', marginTop: 4 }}>{selected.email}</div>
                  {selected.phone && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 2 }}>
                      <i className="bi bi-telephone me-1" />{selected.phone}
                    </div>
                  )}
                </div>
                <button onClick={() => setConfirmDelete(selected)}
                  style={{ background: '#FDECEA', border: '1px solid #F5C6CB', color: 'var(--danger)', padding: '7px 12px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13 }}>
                  <i className="bi bi-trash" />
                </button>
              </div>

              {selected.subject && (
                <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--gold-bg)', border: '1px solid var(--gold-border)', borderRadius: 'var(--radius)' }}>
                  <span style={{ fontSize: 11, color: 'var(--gold-dark)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Subject: </span>
                  <span style={{ color: 'var(--text)', fontSize: '0.875rem' }}>{selected.subject}</span>
                </div>
              )}

              <div style={{ color: 'var(--text-2)', lineHeight: 1.9, whiteSpace: 'pre-line', fontSize: '0.95rem' }}>
                {selected.message}
              </div>

              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--border)', fontSize: '0.75rem', color: 'var(--text-light)' }}>
                Received: {new Date(selected.created_at).toLocaleString()} · IP: {selected.ip_address}
              </div>

              <a href={`mailto:${selected.email}?subject=Re: ${selected.subject || 'Your inquiry'}`}
                className="btn-primary-styled d-inline-flex mt-3" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-reply" /> Reply via Email
              </a>
            </div>
          ) : (
            <div className="admin-card">
              <EmptyState icon="bi-envelope-open" title="Select a message" subtitle="Click any message on the left to read it." />
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          message={`Delete message from "${confirmDelete.name}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ── TESTIMONIALS PAGE ─────────────────────────────────────────
export function AdminTestimonialsPage() {
  const [items, setItems]     = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({ client_name: '', client_title: '', client_company: '', message: '', rating: 5, project_id: '', is_featured: 1, status: 'active' });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { addToast } = useToast();

  const load = () => {
    setLoading(true);
    api.get('/testimonials?admin=1').then(r => setItems(r.data.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/projects?admin=1&limit=50').then(r => setProjects(r.data.data?.projects || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ client_name: '', client_title: '', client_company: '', message: '', rating: 5, project_id: '', is_featured: 1, status: 'active' });
    setShowForm(true);
  };
  const openEdit = t => { setEditing(t); setForm({ ...t, project_id: t.project_id || '' }); setShowForm(true); };

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/testimonials/${editing.id}`, form); addToast('Updated!', 'success'); }
      else { await api.post('/testimonials', form); addToast('Created!', 'success'); }
      setShowForm(false);
      load();
    } catch { addToast('Failed to save', 'error'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/testimonials/${confirmDelete.id}`); addToast('Deleted', 'success'); setConfirmDelete(null); load(); }
    catch { addToast('Failed', 'error'); }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 4 }}>Testimonials</h2>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.875rem' }}>Client reviews and testimonials</p>
        </div>
        <button onClick={openCreate} className="btn-primary-styled">
          <i className="bi bi-plus-lg" /> Add Testimonial
        </button>
      </div>

      {loading ? <Loader /> : (
        <div className="row g-3">
          {items.length === 0 && <div className="col-12"><EmptyState icon="bi-chat-quote" title="No testimonials yet" /></div>}
          {items.map(t => (
            <div key={t.id} className="col-md-6">
              <div className="admin-card p-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: '0.95rem' }}>{t.client_name}</div>
                    <div style={{ color: 'var(--gold-dark)', fontSize: '0.8rem', marginTop: 2 }}>
                      {t.client_title}{t.client_company ? `, ${t.client_company}` : ''}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {[...Array(t.rating || 5)].map((_, i) => (
                        <i key={i} className="bi bi-star-fill me-1" style={{ color: 'var(--gold)', fontSize: 11 }} />
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => openEdit(t)}
                      style={{ background: '#EBF4FD', border: '1px solid #BDE0F8', color: '#2980B9', padding: '5px 9px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 12 }}>
                      <i className="bi bi-pencil" />
                    </button>
                    <button onClick={() => setConfirmDelete(t)}
                      style={{ background: '#FDECEA', border: '1px solid #F5C6CB', color: 'var(--danger)', padding: '5px 9px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 12 }}>
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.7, margin: '0 0 14px', fontStyle: 'italic' }}>
                  "{t.message.length > 160 ? t.message.substring(0, 160) + '…' : t.message}"
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className={t.status === 'active' ? 'badge-success-light' : 'badge-secondary-light'}>{t.status}</span>
                  {t.is_featured === 1 && <span className="badge-gold-light"><i className="bi bi-star-fill me-1" />Featured</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h5 style={{ color: 'var(--text)', margin: 0, fontFamily: 'var(--font-display)' }}>
                {editing ? 'Edit Testimonial' : 'New Testimonial'}
              </h5>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>
                <i className="bi bi-x" />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-sm-6">
                    <label className="form-label-styled">Client Name *</label>
                    <input className="form-control-styled" required value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label-styled">Title / Role</label>
                    <input className="form-control-styled" placeholder="CEO, Director..." value={form.client_title} onChange={e => setForm(f => ({ ...f, client_title: e.target.value }))} />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label-styled">Company</label>
                    <input className="form-control-styled" value={form.client_company} onChange={e => setForm(f => ({ ...f, client_company: e.target.value }))} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label-styled">Rating</label>
                    <select className="form-control-styled" value={form.rating} onChange={e => setForm(f => ({ ...f, rating: parseInt(e.target.value) }))}>
                      {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                    </select>
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label-styled">Featured</label>
                    <select className="form-control-styled" value={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: parseInt(e.target.value) }))}>
                      <option value={1}>Yes</option>
                      <option value={0}>No</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label-styled">Message *</label>
                    <textarea className="form-control-styled" rows={4} required value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label-styled">Linked Project</label>
                    <select className="form-control-styled" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>
                      <option value="">None</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label-styled">Status</label>
                    <select className="form-control-styled" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline-secondary-styled">Cancel</button>
                <button type="submit" className="btn-primary-styled">
                  <i className="bi bi-check2 me-1" />{editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={`Delete testimonial from "${confirmDelete.client_name}"?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
